import { projects } from "./projects";
import { experiences } from "./experience";

/**
 * Strings are derived from the evidence itself, never hand-placed: a project that lists a
 * tool in its stack gets a red string to that tool, and a thread to the employer it shipped at.
 * Each id below matches a `data-node` on a pinned card; positions are measured from the DOM.
 */
export const nodeId = {
  subject: "subject",
  report: "report",
  tech: (id) => `tech:${id}`,
  project: (id) => `project:${id}`,
  job: (id) => `job:${id}`,
  school: (id) => `school:${id}`,
  org: (id) => `org:${id}`,
};

/** The tools that recur across exhibits. `stack` is how they are spelled in project data. */
export const techNodes = [
  { id: "nextjs", label: "Next.js", stack: "Next.js" },
  { id: "vue", label: "Vue 3", stack: "Vue 3" },
  { id: "ghl", label: "GoHighLevel", stack: "GoHighLevel" },
  { id: "supabase", label: "Supabase", stack: "Supabase" },
  { id: "xano", label: "XANO", stack: "XANO" },
  { id: "firebase", label: "Firebase", stack: "Firebase" },
].map((tech) => ({
  ...tech,
  exhibits: projects.filter((project) => project.stack.includes(tech.stack)).length,
}));

/** string = built with · thread = shipped at · twine = leads back to the subject */
const link = (from, to, type) => ({ id: `${from}__${to}`, from, to, type });

const jobIds = new Set(experiences.map((job) => job.id));
const currentJob = experiences.find((job) => job.current);

const employersOf = (project) =>
  (project.companyIds ?? (project.companyId ? [project.companyId] : [])).filter((id) => jobIds.has(id));

export const connections = [
  ...techNodes.flatMap((tech) =>
    projects
      .filter((project) => project.stack.includes(tech.stack))
      .map((project) => link(nodeId.tech(tech.id), nodeId.project(project.id), "string"))
  ),
  ...projects.flatMap((project) =>
    employersOf(project).map((jobId) => link(nodeId.job(jobId), nodeId.project(project.id), "thread"))
  ),
  ...(currentJob ? [link(nodeId.subject, nodeId.job(currentJob.id), "twine")] : []),
  link(nodeId.subject, nodeId.tech("nextjs"), "twine"),
  link(nodeId.subject, nodeId.tech("vue"), "twine"),
  link(nodeId.subject, nodeId.school("cvsu"), "twine"),
  link(nodeId.subject, nodeId.org("lds"), "twine"),
  link(nodeId.subject, nodeId.report, "twine"),
];

/** Every node a given node is tied to, for tracing a string from either end. */
export const linkedNodes = connections.reduce((map, connection) => {
  (map[connection.from] ||= new Set()).add(connection.to);
  (map[connection.to] ||= new Set()).add(connection.from);
  return map;
}, {});

/** A pinned string sags a little; longer runs sag more. */
export const stringPath = (from, to, seed = 1) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  const sag = Math.min(90, length * 0.07) + (seed % 5) * 4;
  const cx = from.x + dx / 2;
  const cy = from.y + dy / 2 + sag;
  return `M ${from.x.toFixed(1)} ${from.y.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${to.x.toFixed(1)} ${to.y.toFixed(1)}`;
};
